import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { PaymentPlanItem, PaymentStatus, PaymentConcept, PAYMENT_CONCEPT_LABELS, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from '../../types/payment-plan';
import { CommissionPaymentPlan } from '../../types/commissionPaymentPlan';
import CommissionPaymentPlansAPI, { toFrontend } from '../../api/CommissionPaymentPlansAPI';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Check, X } from 'lucide-react';
import PriceField from '../PriceField/PriceField';
import SelectInput from '../SelectInput/SelectInput';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import PaymentPlanButton from '../PaymentPlanButton/PaymentPlanButton';
import { Calendar } from 'lucide-react';
import { useCommissionContractTemplate } from '../../hooks/useCommissionContractTemplate';
import InputShortText from '../InputShortText/InputShortText';
import InputNumber from '../InputNumber/InputNumber';
import TextArea from '../TextArea/TextArea';
import Pill from '../Pill/Pill';
import PaymentStatusToggle from '../PaymentStatusToggle/PaymentStatusToggle';
import { useTranslation } from 'react-i18next';
import CONFIG from '../../config/config';

interface CommissionPaymentPlanManagerProps {
  paymentPlan?: CommissionPaymentPlan;
  onSave: (paymentPlan: CommissionPaymentPlan) => void;
  onCancel: () => void;
  isEditable?: boolean;
  contractId?: number;
}

export interface CommissionPaymentPlanManagerRef {
  save: () => void;
}

const CommissionPaymentPlanManager = forwardRef<CommissionPaymentPlanManagerRef, CommissionPaymentPlanManagerProps>(({
  paymentPlan,
  onSave,
  isEditable = true,
  contractId
}, ref) => {
  const { t } = useTranslation();
  const { contractTemplate } = useCommissionContractTemplate();
  const [items, setItems] = useState<PaymentPlanItem[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('EUR');
  const [isEditMode, setIsEditMode] = useState(false);
  const [dueDateEditIds, setDueDateEditIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (paymentPlan) {
      setItems(paymentPlan.items);
      setTotalAmount(paymentPlan.totalAmount);
      setCurrency(paymentPlan.currency);
    } else {
      const defaultAmount = contractTemplate?.dropFee?.price ?? 50;
      const defaultCurrency = contractTemplate?.dropFee?.currency ?? 'EUR';
      const defaultItem: PaymentPlanItem = {
        id: null,
        concept: PaymentConcept.DROP_FEE,
        amount: defaultAmount,
        currency: defaultCurrency,
        status: PaymentStatus.NOT_PAID,
        isRequired: true,
        validForDays: contractTemplate?.saleDuration,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        description: t('commission_payment_plan.default_drop_fee_description') || 'Initial fee to list your dress for sale'
      };
      setItems([defaultItem]);
      setTotalAmount(defaultAmount);
      setCurrency(defaultCurrency);
    }
  }, [paymentPlan, t, contractTemplate?.dropFee?.price, contractTemplate?.dropFee?.currency, contractTemplate?.saleDuration]);

  useEffect(() => {
    setIsEditMode(!!isEditable);
  }, [isEditable]);

  useEffect(() => {
    const newTotal = items.reduce((sum, item) => sum + item.amount, 0);
    setTotalAmount(newTotal);
  }, [items]);

  const toggleDueDateField = (index: number) => {
    setDueDateEditIds(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const removePaymentItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    
    if (paymentPlan?.id && contractId) {
      const updatedPlan: CommissionPaymentPlan = {
        ...paymentPlan,
        items: updatedItems,
        totalAmount: updatedItems.reduce((sum, item) => sum + item.amount, 0),
        updatedAt: new Date().toISOString()
      };
      
      CommissionPaymentPlansAPI.update(contractId, updatedPlan)
        .then((resp: any) => {
          setItems(toFrontend(resp.data).items);
        })
        .catch((e: any) => {
          console.error('Failed to delete payment item:', e);
          setItems(items);
        });
    }
  };

  const updatePaymentItem = (index: number, updates: Partial<PaymentPlanItem>) => {
    setItems(items.map((item, i) => 
      i === index 
        ? { ...item, ...updates, updatedAt: new Date().toISOString() }
        : item
    ));
  };

  const updateItemAmount = (index: number, amount: number) => {
    updatePaymentItem(index, { amount });
  };

  const markAsPaid = (index: number) => {
    updatePaymentItem(index, { 
      status: PaymentStatus.PAID, 
      paidDate: new Date().toISOString() 
    });
  };

  const markAsUnpaid = (index: number) => {
    updatePaymentItem(index, { 
      status: PaymentStatus.NOT_PAID, 
      paidDate: undefined 
    });
  };

  const handleSave = () => {
    const plan: CommissionPaymentPlan = {
      id: paymentPlan?.id || null,
      contractId: contractId || 0,
      items,
      totalAmount,
      currency,
      createdAt: paymentPlan?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    onSave(plan);
  };

  useImperativeHandle(ref, () => ({
    save: handleSave
  }));

  const getConceptOptions = () => {
    return Object.values(PaymentConcept).map(concept => ({
      value: concept,
      readableValue: t(PAYMENT_CONCEPT_LABELS[concept]) || concept
    }));
  };

  const getDefaultValidForDays = () => {
    return contractTemplate?.saleDuration ?? 90;
  };

  const getStatusColor = (status: PaymentStatus) => {
    return PAYMENT_STATUS_COLORS[status] as any;
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header removed - title is provided by SlideOver */}

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">
            {t('commission_payment_plan.total_amount') || 'Total Amount'}:
          </span>
          <span className="text-lg font-bold text-gray-900">
            {totalAmount.toFixed(2)} {currency}
          </span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm text-gray-600">
            {t('commission_payment_plan.payments_count') || 'Payments'}:
          </span>
          <span className="text-sm text-gray-600">
            {items.length}
          </span>
        </div>
      </div>

      {/* Payment Items */}
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={index} className="border rounded-lg p-4 bg-white">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-2">
                  {t('commission_payment_plan.payment') || 'Payment'} {index + 1}
                </h4>
                
                {/* Status and Actions */}
                <div className="flex items-center gap-2 mb-3">
                  <Pill
                    text={t(PAYMENT_STATUS_LABELS[item.status]) || item.status}
                    color={getStatusColor(item.status)}
                  />
                  {isOverdue(item.dueDate) && item.status !== PaymentStatus.PAID && (
                    <Pill
                      text={t('commission_payment_plan.overdue') || 'Overdue'}
                      color="red"
                    />
                  )}
                  {isEditable && isEditMode && (
                    <PaymentStatusToggle
                      id={index}
                      paid={item.status === PaymentStatus.PAID}
                      onPaymentStatusChange={async (_id, paid) => {
                        if (paid) {
                          markAsPaid(index);
                        } else {
                          markAsUnpaid(index);
                        }
                      }}
                      size="sm"
                    />
                  )}
                </div>
              </div>
              
              {isEditable && isEditMode && item.status !== PaymentStatus.PAID && items.length > 1 && (
                <ButtonV2
                  variant="outline"
                  size="sm"
                  onClick={() => removePaymentItem(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </ButtonV2>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Concept */}
              <div>
                {isEditMode && item.status !== PaymentStatus.PAID ? (
                  <SelectInput
                    label={t('commission_payment_plan.concept') || 'Concept'}
                    values={getConceptOptions()}
                    defaultValue={item.concept}
                    onChange={(e) => updatePaymentItem(index, { concept: e.target.value as PaymentConcept })}
                    isDisabled={false}
                  />
                ) : (
                  <InputShortText
                    label={t('commission_payment_plan.concept') || 'Concept'}
                    defaultValue={t(PAYMENT_CONCEPT_LABELS[item.concept as PaymentConcept]) || item.concept}
                    isDisabled
                    onChange={() => {}}
                  />
                )}
              </div>

              {/* Amount */}
              <div>
                {isEditMode && item.status !== PaymentStatus.PAID ? (
                  <PriceField
                    label={t('commission_payment_plan.amount') || 'Amount'}
                    currency={item.currency}
                    price={item.amount}
                    currencies={CONFIG.SUPPORTED_CURRENCIES}
                    onCurrencyChange={(newCurrency) => {
                      updatePaymentItem(index, { currency: newCurrency });
                      setCurrency(newCurrency);
                    }}
                    onPriceChange={(amount) => updateItemAmount(index, amount)}
                    isDisabled={false}
                  />
                ) : (
                  <InputShortText
                    label={t('commission_payment_plan.amount') || 'Amount'}
                    defaultValue={`${item.amount.toFixed(2)} ${item.currency}`}
                    isDisabled
                    onChange={() => {}}
                  />
                )}
              </div>

              {/* Valid for (days) */}
              <div>
                {isEditMode && item.status !== PaymentStatus.PAID ? (
                  <InputNumber
                    label={t('store_commission_panel.commission_payment_plan.valid_for_days') || 'Valid for (days)'}
                    defaultValue={item.validForDays ?? getDefaultValidForDays()}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePaymentItem(index, { validForDays: Number(e.currentTarget.value) })}
                    isDisabled={false}
                    validationFn={(value: number) => value >= 0}
                    errorMessage="errors.minimum_zero"
                  />
                ) : (
                  <InputShortText
                    label={t('store_commission_panel.commission_payment_plan.valid_for_days') || 'Valid for (days)'}
                    defaultValue={String(item.validForDays ?? getDefaultValidForDays())}
                    isDisabled
                    onChange={() => {}}
                  />
                )}
              </div>

              {/* Due Date - optional assign flow (full width on md+) */}
              <div className="md:col-span-2">
                {isEditMode && item.status !== PaymentStatus.PAID ? (
                  (!dueDateEditIds.has(index) && !item.dueDate) ? (
                    <PaymentPlanButton
                      text={t('payment_plan.assign_due_date') || 'Assign a due date'}
                      onClick={() => toggleDueDateField(index)}
                      disabled={false}
                      actionName="Assign Due Date"
                      icon={<Calendar className="h-4 w-4" />}
                      variant="blue"
                    />
                  ) : (
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <CustomDatePicker
                          label={t('commission_payment_plan.due_date') || 'Due Date'}
                          value={item.dueDate ? new Date(item.dueDate) : null}
                          onChange={(date) => updatePaymentItem(index, { 
                            dueDate: date ? date.toISOString().split('T')[0] : undefined 
                          })}
                          isDisabled={false}
                          dateFormat="dd/MM/yyyy"
                          showTimeSelect={false}
                          disablePast={false}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => { 
                          updatePaymentItem(index, { dueDate: undefined }); 
                          // Remove from edit mode when deleting due date
                          setDueDateEditIds(prev => {
                            const next = new Set(prev);
                            next.delete(index);
                            return next;
                          });
                        }}
                        className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors mb-[2px]"
                        title={t('store_commission_panel.remove_addon') || 'Remove'}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )
                ) : (
                  <div>
                    <InputShortText
                      label={t('commission_payment_plan.due_date') || 'Due Date'}
                      defaultValue={item.dueDate ? new Date(item.dueDate).toLocaleDateString() : t('commission_payment_plan.no_due_date') || 'No due date'}
                      isDisabled
                      onChange={() => {}}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="mt-4">
              {isEditMode && item.status !== PaymentStatus.PAID ? (
                <TextArea
                  label={t('commission_payment_plan.description') || 'Description (Optional)'}
                  defaultValue={item.description || ''}
                  onChange={(e) => updatePaymentItem(index, { description: e.target.value })}
                  isDisabled={false}
                  placeholder={t('commission_payment_plan.description_placeholder') || 'Add a description for this payment...'}
                  maxLength={100}
                />
              ) : (
                item.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('commission_payment_plan.description') || 'Description'}
                    </label>
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {item.description}
                    </p>
                  </div>
                )
              )}
            </div>

            {/* Payment Info (if paid) */}
            {item.status === PaymentStatus.PAID && item.paidDate && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {t('commission_payment_plan.paid_on') || 'Paid on'}: {new Date(item.paidDate).toLocaleDateString()}
                  </span>
                </div>
                {item.paymentMethod && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('commission_payment_plan.payment_method') || 'Method'}: {item.paymentMethod}
                  </p>
                )}
                {item.invoiceReference && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('commission_payment_plan.invoice_reference') || 'Reference'}: {item.invoiceReference}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

    </div>
  );
});

CommissionPaymentPlanManager.displayName = 'CommissionPaymentPlanManager';

export default CommissionPaymentPlanManager;
